import { useMatch, useParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import moment from 'moment'

import Loading from 'modules/core/components/Loading'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { useCurrentIncidence } from 'modules/bookkeepers/lib/CurrentIncidenceContext'
import { useIncidenceDetail } from 'modules/bookkeepers/lib/IncidenceDetailContext'
import type IncidenceDetail from 'modules/bookkeepers/store/models/IncidenceDetail'
import TabsSelector from 'modules/payroll_bundle/components/TabsSelector'
import useFetchIncidenceChangeItems from 'modules/payroll_bundle/hooks/useFetchIncidenceChangeItems'
import { useUpdateIncidenceReadAt } from 'modules/payroll_bundle/hooks/useUpdateIncidenceReadAt'
import { INCIDENCE_TYPES } from 'modules/payroll_bundle/lib/Incidences/Types'

import DeletedInfo from '../IncidenceDetailPage/_DeletedInfo'
import IncidenceHistoryList from './_IncidenceHistoryList'

const isDeletableTimeoffIncidence = (incidenceType: string) => {
  return [INCIDENCE_TYPES.sick, INCIDENCE_TYPES.parental, INCIDENCE_TYPES.custom_leave].includes(
    incidenceType
  )
}

const isDeletableTerminationIncidence = (incidenceType: string): boolean => {
  return incidenceType === INCIDENCE_TYPES.termination
}

const isDeletableContractIncidence = (incidenceType: string): boolean => {
  return incidenceType === INCIDENCE_TYPES.contract
}

const isDeletableIncidenceType = (incidenceType: string): boolean => {
  return (
    isDeletableTimeoffIncidence(incidenceType) ||
    isDeletableTerminationIncidence(incidenceType) ||
    isDeletableContractIncidence(incidenceType)
  )
}

const getDeletableIncidenceLabel = (
  incidenceType: string,
  incidenceDetailModel: IncidenceDetail
): string | null => {
  if (isDeletableTimeoffIncidence(incidenceType) && !incidenceDetailModel.id)
    return i18n.t('bookkeepers.incidence.details.deleted_time_off')

  if (
    isDeletableTerminationIncidence(incidenceType) &&
    !incidenceDetailModel.attributes.terminated_on
  )
    return i18n.t('bookkeepers.incidence.details.deleted_termination')

  if (isDeletableContractIncidence(incidenceType) && !incidenceDetailModel.id)
    return i18n.t('bookkeepers.incidence.details.deleted_contract_version')

  return null
}

const IncidenceHistoryPage = () => {
  const { navigation } = useNavigation()

  const { id } = useParams<'id'>()
  const incidenceId = Number(id)
  const incidence = useCurrentIncidence()
  const isBookkeeperView = !!useMatch({ path: navigation.bookkeepers.fullPath(), end: false })
  const { update } = useUpdateIncidenceReadAt()

  const { loading, changeItems } = useFetchIncidenceChangeItems(
    incidenceId,
    isBookkeeperView,
    incidence?.name ?? null,
    incidence?.targetId ?? null,
    incidence
  )

  const markAsRead = (incidenceId: number) => {
    const variables = {
      markAsRead: true,
      incidenceId: incidenceId,
    }

    update(variables)
  }

  useEffect(() => {
    if (isBookkeeperView && incidenceId && !incidence.readAt) {
      markAsRead(incidenceId)
      incidence.readAt = dateToString(moment(), 'backend_datetime')
    }
  }, [incidenceId, incidence.readAt, isBookkeeperView])

  const { loading: loadingDetails, incidenceDetailModel } = useIncidenceDetail()

  const incidenceType = incidence?.name as keyof typeof INCIDENCE_TYPES
  const isDeletableIncidence = isDeletableIncidenceType(incidenceType)
  const deletableIncidenceLabel = isDeletableIncidence
    ? getDeletableIncidenceLabel(incidenceType, incidenceDetailModel)
    : null

  return (
    <>
      <TabsSelector
        selectedTab="updates"
        incidenceId={incidenceId}
        isBookkeeper={isBookkeeperView}
      />
      <Box padding="s24" overflowY="auto">
        {loading || loadingDetails ? (
          <Loading />
        ) : deletableIncidenceLabel ? (
          <DeletedInfo label={deletableIncidenceLabel} />
        ) : (
          <IncidenceHistoryList changeItems={changeItems} />
        )}
      </Box>
    </>
  )
}

export default IncidenceHistoryPage
